import React from 'react';
import Close from '@mui/icons-material/Close';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

export default function Chip(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  return {
    MuiChip: {
      defaultProps: {
        deleteIcon: <Close />
      },

      styleOverrides: {
        root: {
          borderRadius: 100, // full pill
          fontWeight: theme.typography.fontWeightSemiBold,
          transition: theme.transitions.create(['background-color', 'color', 'border-color'], {
            duration: theme.transitions.duration.shortest
          })
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.75rem'
        },
        sizeMedium: {
          height: 28,
          fontSize: '0.8125rem'
        },
        // Default filled chip — neutral unselected
        filled: {
          backgroundColor: isLight ? theme.palette.grey[200] : theme.palette.grey[700],
          color: isLight ? theme.palette.text.secondary : theme.palette.common.white,
          '&:hover': {
            backgroundColor: isLight ? theme.palette.grey[300] : theme.palette.grey[600]
          },
          // Sana.ai style: alpha tint bg + main color text (both from .main, lighter feel)
          '&.MuiChip-colorPrimary': {
            backgroundColor: alpha(theme.palette.primary.main, theme.alphas.chip),
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.alphas.chipHover)
            }
          },
          // secondary.main is a dark navy — fine as text in light mode, unreadable on a
          // dark background, so dark mode uses the lighter swatch instead
          '&.MuiChip-colorSecondary': {
            backgroundColor: alpha(theme.palette.secondary.main, theme.alphas.chip),
            color: isLight ? theme.palette.secondary.main : theme.palette.secondary.light,
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, theme.alphas.chipHover)
            }
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha(theme.palette.success.main, theme.alphas.chip),
            color: theme.palette.success.main
          },
          // Warning: keep dark text — .main (#FFC107) has poor contrast on light yellow
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha(theme.palette.warning.main, 0.14),
            color: theme.palette.warning.dark
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha(theme.palette.error.main, theme.alphas.chip),
            color: theme.palette.error.main
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: alpha(theme.palette.info.main, theme.alphas.chip),
            color: theme.palette.info.main
          }
        },
        // Outlined — clean border, no fill
        outlined: {
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.grey[400]
          },
          '&.MuiChip-colorPrimary': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.alphas.hover)
            }
          },
          '&.MuiChip-colorSecondary': {
            borderColor: isLight ? theme.palette.secondary.main : theme.palette.secondary.light,
            color: isLight ? theme.palette.secondary.main : theme.palette.secondary.light
          }
        },
        colorDefault: {
          '& .MuiChip-avatarMedium, .MuiChip-avatarSmall': {
            color: theme.palette.text.secondary
          }
        },
        label: {
          paddingLeft: 10,
          paddingRight: 10
        },
        labelSmall: {
          paddingLeft: 8,
          paddingRight: 8
        }
      }
    }
  };
}
